import React, { useState } from 'react';
import { FaListAlt, FaTrash, FaEye, FaSearch, FaTimesCircle, FaSpinner, FaEdit, FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getCategory, updateCategory, deleteCategory } from '../../services/categoriesApi';
import './ControlPanel.css';
import Spinner from '../Spinner/Spinner';
import {translateToEnglish} from "../../utils/translations";
import AddCategory from "./AddCategory";
import { useCategories } from "../../utils/CategoriesContext";

const CategoryManagement = () => {
    const { allCategories, loadingCategories, refreshCategories } = useCategories();
    // Catálogo y loading vienen del context (prefetcheado al iniciar app).
    const categories = allCategories;
    const loading = loadingCategories;
    const fetchCategories = refreshCategories;
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [categoryDetails, setCategoryDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState({});
    const [editedSubcategories, setEditedSubcategories] = useState([]);
    const [newSubcategory, setNewSubcategory] = useState('');
    const [saveLoading, setSaveLoading] = useState(false);
    const [editedName, setEditedName] = useState('');
    const [editedType, setEditedType] = useState('');
    const [editedHasProviders, setEditedHasProviders] = useState(false);
    const [editedProviderCount, setEditedProviderCount] = useState(0);

    // Función para obtener detalles de una categoría
    const handleViewDetails = async (category) => {
        setSelectedCategory(category);
        setShowModal(true);
        setLoadingDetails(true);

        try {
            const details = await getCategory(category.type, category.name);
            setCategoryDetails(details);
            setEditedSubcategories(details.subcategories || []);
            setEditedName(details.name || category.name);
            setEditedType(details.type || category.type);
            setEditedHasProviders(details.hasProviders || false);
            setEditedProviderCount(details.providerCount || 0);
        } catch (error) {
            console.error('Error al cargar detalles de la categoría:', error);
            toast.error('No se pudieron cargar los detalles de la categoría');
            setShowModal(false);
        } finally {
            setLoadingDetails(false);
        }
    };

    // Función para eliminar una categoría
    const handleDeleteCategory = async (category) => {
        const categoryIdentifier = `${category.name}-${category.type}`;
        const categoryId = category.id || category._id;

        if (!categoryId) {
            toast.error('No se puede eliminar la categoría: ID no disponible');
            return;
        }

        if (window.confirm(`¿Estás seguro de que deseas eliminar la categoría ${category.name} (${category.type})?`)) {
            setDeleteLoading(prev => ({ ...prev, [categoryIdentifier]: true }));
            try {
                await deleteCategory(categoryId); // Usar el ID real de la categoría
                toast.success('Categoría eliminada correctamente');
                // Refrescamos el catálogo global desde el context
                fetchCategories();
            } catch (error) {
                console.error('Error al eliminar categoría:', error);
                toast.error('No se pudo eliminar la categoría');
            } finally {
                setDeleteLoading(prev => ({ ...prev, [categoryIdentifier]: false }));
            }
        }
    };

    // Función para guardar los cambios en las subcategorías
    const handleSaveChanges = async () => {
        if (!categoryDetails) return;

        // Validar que el nombre de la categoría no contenga guiones
        if (editedName.includes('-')) {
            toast.error('El nombre de la categoría no puede contener guiones (-)');
            return;
        }

        // Validar que las subcategorías no contengan guiones
        const subcategoriesWithDashes = editedSubcategories.filter(sub => sub.includes('-'));
        if (subcategoriesWithDashes.length > 0) {
            toast.error(`Las siguientes subcategorías no pueden contener guiones (-): ${subcategoriesWithDashes.join(', ')}`);
            return;
        }

        setSaveLoading(true);
        try {
            const updatedCategory = {
                ...categoryDetails,
                name: editedName.trim(),
                type: translateToEnglish(editedType),
                subcategories: editedSubcategories.map(sub => sub.trim()),
                hasProviders: editedHasProviders,
                providerCount: parseInt(editedProviderCount) || 0
            };
            await updateCategory(selectedCategory.id, updatedCategory);
            toast.success('Categoría actualizada correctamente');
            setShowModal(false);

            // Recargar la lista de categorías para reflejar los cambios
            fetchCategories();
        } catch (error) {
            console.error('Error al actualizar categoría:', error);

            // Manejo especial para errores de CONFLICT
            if (error.response?.status === 409 || error.response?.data?.error?.includes('CONFLICT') || error.message?.includes('CONFLICT')) {
                toast.error(`Ya existe una categoría llamada "${editedName}" para el tipo "${editedType}". Por favor, elige un nombre diferente.`);
            } else {
                toast.error('No se pudo actualizar la categoría');
            }
        } finally {
            setSaveLoading(false);
        }
    };

    // Funciones para editar subcategorías
    const handleAddSubcategory = () => {
        if (newSubcategory.trim() === '') return;

        // Validar que la subcategoría no contenga guiones
        if (newSubcategory.trim().includes('-')) {
            toast.error('El nombre de la subcategoría no puede contener guiones (-)');
            return;
        }

        if (editedSubcategories.includes(newSubcategory.trim())) {
            toast.warning('Esta subcategoría ya existe');
            return;
        }
        setEditedSubcategories([...editedSubcategories, newSubcategory.trim()]);
        setNewSubcategory('');
    };

    const handleRemoveSubcategory = (index) => {
        const newSubcategories = [...editedSubcategories];
        newSubcategories.splice(index, 1);
        setEditedSubcategories(newSubcategories);
    };

    // Filtrar categorías por término de búsqueda
    const filteredCategories = categories.filter(category => {
        return category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            category.type.toLowerCase().includes(searchTerm.toLowerCase());
    });

    return (
        <>
            {loading && (<Spinner/>)}
            <div className="control-panel-row">
                {/* Columna principal - tabla o lista de categorías */}
                <div className="control-panel-card wide-card">
                    {/* Aquí iría tu tabla o contenido principal */}
                    <div className="card-header">Listado de Categorías</div>
                    <div className="card-content">
                        <div className="form-group" style={{marginBottom: '15px'}}>
                            <div style={{display: 'flex', alignItems: 'center'}}>
                                <div style={{position: 'relative', flex: 1}}>
                                    <FaSearch style={{
                                        position: 'absolute',
                                        left: '10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#aaa'
                                    }}/>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Buscar categorías..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{paddingLeft: '35px'}}
                                    />
                                </div>
                                <button
                                    className="provider-btn provider-btn-outline-primary"
                                    onClick={fetchCategories}
                                    style={{marginLeft: '10px'}}
                                >
                                    Actualizar
                                </button>
                            </div>
                        </div>

                        {/* Tabla de categorías */}
                        {loading ? (
                            <div style={{textAlign: 'center', padding: '20px'}}>
                                <FaSpinner className="fa-spin" style={{fontSize: '2rem', color: '#ccc'}}/>
                                <p>Cargando categorías...</p>
                            </div>
                        ) : (
                            <div style={{overflowX: 'auto'}}>
                                <table className="providers-table">
                                    <thead>
                                    <tr>
                                        <th style={{width: '30%'}}>Nombre</th>
                                        <th style={{width: '25%'}}>ID</th>
                                        <th style={{width: '20%'}}>Tipo</th>
                                        <th style={{width: '25%'}}>Acciones</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {filteredCategories.length > 0 ? (
                                        filteredCategories.map((category) => {
                                            const categoryIdentifier = `${category.name}-${category.type}`;
                                            const categoryId = category.id || category._id || 'N/A';
                                            return (
                                                <tr key={categoryIdentifier}>
                                                    <td>{category.name}</td>
                                                    <td>
                                                        <span style={{
                                                            fontSize: '0.85rem',
                                                            color: '#666',
                                                            fontFamily: 'monospace',
                                                            backgroundColor: '#f5f5f5',
                                                            padding: '2px 6px',
                                                            borderRadius: '4px',
                                                            wordBreak: 'break-all'
                                                        }}>
                                                            {categoryId}
                                                        </span>
                                                    </td>
                                                    <td>{category.type}</td>
                                                    <td>
                                                        <div className="provider-action-buttons">
                                                            <button
                                                                className="provider-action-btn"
                                                                onClick={() => handleViewDetails(category)}
                                                            >
                                                                <FaEye/>
                                                                <span className="provider-action-text">Ver/Editar</span>
                                                            </button>
                                                            <button
                                                                className="provider-action-btn"
                                                                style={{backgroundColor: '#d9534f'}}
                                                                onClick={() => handleDeleteCategory(category)}
                                                                disabled={deleteLoading[categoryIdentifier]}
                                                            >
                                                                {deleteLoading[categoryIdentifier] ? (
                                                                    <FaSpinner className="fa-spin"/>
                                                                ) : (
                                                                    <FaTrash/>
                                                                )}
                                                                <span className="provider-action-text">Eliminar</span>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="3" style={{textAlign: 'center', padding: '20px'}}>
                                                <FaTimesCircle
                                                    style={{fontSize: '1.5rem', color: '#aaa', marginBottom: '10px'}}/>
                                                <p>No se encontraron categorías</p>
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
                <AddCategory onAdded={fetchCategories} />
                {/* Modal de detalles y edición de categoría */}
                {showModal && (
                    <div className="provider-modal-backdrop">
                        <div className="provider-modal-container provider-contact-modal">
                            <div className="provider-modal-header">
                                <h3>Detalles de la Categoría</h3>
                                <button className="provider-modal-close" onClick={() => setShowModal(false)}>
                                ✕
                            </button>
                        </div>
                        <div className="provider-modal-body">
                            {loadingDetails ? (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <FaSpinner className="fa-spin" style={{ fontSize: '2.5rem', color: '#6c63ff' }} />
                                    <p style={{ marginTop: '15px', color: '#6c757d' }}>Cargando detalles...</p>
                                </div>
                            ) : categoryDetails ? (
                                <div className="category-edit-form">
                                    {/* Información básica */}
                                    <div className="form-section">
                                        <h4 className="section-title">
                                            <FaEdit style={{ marginRight: '8px', color: '#6c63ff' }} />
                                            Información Básica
                                        </h4>
                                        <div className="form-grid">
                                            <div className="form-group">
                                                <label className="form-label">
                                                    <strong>ID de la Categoría:</strong>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={categoryDetails.id || categoryDetails._id || 'No disponible'}
                                                    disabled
                                                    style={{
                                                        backgroundColor: '#f8f9fa',
                                                        color: '#6c757d',
                                                        fontFamily: 'monospace',
                                                        fontSize: '0.9rem'
                                                    }}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">
                                                    <strong>Nombre:</strong>
                                                    <span className="required-asterisk"> *</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={editedName}
                                                    onChange={(e) => setEditedName(e.target.value)}
                                                    placeholder="Nombre de la categoría"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">
                                                    <strong>Tipo:</strong>
                                                    <span className="required-asterisk"> *</span>
                                                </label>
                                                <select
                                                    className="form-control"
                                                    value={editedType}
                                                    onChange={(e) => setEditedType(e.target.value)}
                                                >
                                                    <option value="">Seleccionar tipo</option>
                                                    <option value="servicios">Servicios</option>
                                                    <option value="productos">Productos</option>
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">
                                                    <strong>Contador de Proveedores:</strong>
                                                </label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={editedProviderCount}
                                                    onChange={(e) => setEditedProviderCount(e.target.value)}
                                                    min="0"
                                                    placeholder="Número de proveedores"
                                                />
                                            </div>
                                        </div>

                                        {/* Switch para hasProviders */}
                                        <div className="form-group switch-group">
                                            <label className="switch-label">
                                                <strong>¿Tiene proveedores activos?</strong>
                                                <div className="switch-container">
                                                    <input
                                                        type="checkbox"
                                                        className="switch-input"
                                                        checked={editedHasProviders}
                                                        onChange={(e) => setEditedHasProviders(e.target.checked)}
                                                    />
                                                    <span className="switch-slider"></span>
                                                </div>
                                            </label>
                                        </div>
                                    </div>

                                    {/* Subcategorías */}
                                    <div className="form-section">
                                        <h4 className="section-title">
                                            <FaListAlt style={{ marginRight: '8px', color: '#6c63ff' }} />
                                            Subcategorías ({editedSubcategories.length})
                                        </h4>

                                        {/* Lista de subcategorías existentes */}
                                        <div className="subcategories-list">
                                            {editedSubcategories.length > 0 ? (
                                                <div className="control-panel-subcategory-chips">
                                                    {editedSubcategories.map((subcategory, index) => (
                                                        <div key={index} className="control-panel-subcategory-chip">
                                                            <span className="subcategory-text">{subcategory}</span>
                                                            <button
                                                                className="subcategory-remove"
                                                                onClick={() => handleRemoveSubcategory(index)}
                                                                title="Eliminar subcategoría"
                                                            >
                                                                <FaTimesCircle />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="empty-subcategories">
                                                    <FaListAlt style={{ fontSize: '2rem', color: '#dee2e6', marginBottom: '10px' }} />
                                                    <p>No hay subcategorías definidas</p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Formulario para añadir nueva subcategoría */}
                                        <div className="add-subcategory-section">
                                            <div className="add-subcategory-form">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Escribe una nueva subcategoría..."
                                                    value={newSubcategory}
                                                    onChange={(e) => setNewSubcategory(e.target.value)}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleAddSubcategory();
                                                        }
                                                    }}
                                                />
                                                <button
                                                    className="provider-btn provider-btn-primary add-subcategory-btn"
                                                    onClick={handleAddSubcategory}
                                                    disabled={!newSubcategory.trim()}
                                                >
                                                    <FaPlus />
                                                    <span>Añadir</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <FaTimesCircle style={{ fontSize: '2.5rem', color: '#dc3545', marginBottom: '15px' }} />
                                    <p style={{ color: '#6c757d' }}>No se pudieron cargar los detalles de la categoría.</p>
                                </div>
                            )}
                        </div>
                        <div className="provider-modal-footer">
                            <button
                                className="provider-btn provider-btn-outline"
                                onClick={() => setShowModal(false)}
                            >
                                Cancelar
                            </button>
                            <button
                                className="provider-btn provider-btn-primary"
                                onClick={handleDeleteCategory.bind(null, selectedCategory)}
                                style={{ backgroundColor: '#d9534f', marginRight: '8px' }}
                            >
                                <FaTrash />
                                <span className="provider-btn-text">Eliminar</span>
                            </button>
                            <button
                                className="provider-btn provider-btn-primary"
                                onClick={handleSaveChanges}
                                disabled={saveLoading}
                            >
                                {saveLoading ? (
                                    <FaSpinner className="fa-spin" />
                                ) : (
                                    <FaEdit />
                                )}
                                <span className="provider-btn-text">Guardar Cambios</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
            </div>
        </>
    );
};

export default CategoryManagement;